import { dayjsToSeconds, secondsToDayjs } from '../time-conversions';
import { DEFAULT_VIDEO_DURATION, DURATION_TOLERANCE } from '../constants';
import { getMinCycleDuration } from '../duration-utils';
import type { CycleData, CycleCallback, ValidationError, SideType } from '../../types';

export function findCycleById(cycles: CycleData[], cycleId: string): CycleData | undefined {
	return cycles.find((c) => c.id === cycleId);
}

export function createNewCycle(start: number, end: number, identified = false, side?: SideType): CycleData {
	const id = crypto.randomUUID();
	return { id, start, end, identified, is_justified: true, ...(side && { side }) };
}

export function transformCycles(externalCycles: CycleCallback[]): CycleData[] {
	const beforeFilter = externalCycles.map((externalCycle) => {
		const start = dayjsToSeconds(externalCycle.start_time);
		const end = dayjsToSeconds(externalCycle.end_time);

		const cycleData: CycleData = {
			id: externalCycle.id,
			name: externalCycle.name,
			type_id: externalCycle.type_id,
			start,
			end,
			identified: externalCycle.identified || false,
			is_justified: externalCycle.is_justified,
			side: externalCycle.side,
			paired_cycle_id: externalCycle.paired_cycle_id
		};

		return cycleData;
	});

	const transformed = beforeFilter.filter((cycle) => {
		const isValid = cycle.start < cycle.end;
		return isValid;
	});

	return transformed;
}

export function shouldUseCurrentValue(errors: ValidationError[], videoDuration: number): boolean {
	const hasOnlyBoundsErrors = errors.every((error) => error.type === 'bounds');
	return hasOnlyBoundsErrors && videoDuration === DEFAULT_VIDEO_DURATION;
}

export function updateExternalCycleTime(
	externalCycles: CycleCallback[],
	cycleId: string,
	newStart: number,
	newEnd: number,
	allCycles?: CycleCallback[]
): CycleCallback[] {
	const cyclesToUpdate = allCycles || externalCycles;

	const result = cyclesToUpdate.map((cycle) => {
		if (cycle.id === cycleId) {
			return {
				...cycle,
				start_time: secondsToDayjs(newStart),
				end_time: secondsToDayjs(newEnd),
				is_justified: cycle.is_justified
			};
		}

		const originalCycle = cyclesToUpdate.find((c) => c.id === cycleId);
		if (originalCycle?.paired_cycle_id && cycle.id === originalCycle.paired_cycle_id) {
			return {
				...cycle,
				start_time: secondsToDayjs(newStart),
				end_time: secondsToDayjs(newEnd),
				is_justified: cycle.is_justified
			};
		}

		return cycle;
	});

	return result;
}

export function roundToMilliseconds(value: number): number {
	return Number(value.toFixed(3));
}

export function isValidCycleDuration(duration: number, videoDuration: number): boolean {
	const minDuration = getMinCycleDuration(videoDuration);
	return roundToMilliseconds(duration) >= minDuration - DURATION_TOLERANCE;
}
